import { MoviesModel } from '@modules/entertainment/models/movies.model.ts';
import React, { useEffect, useMemo, useState } from 'react';
import DynamicTable from '@components/dynamic-table/dynamic-table.tsx';
import createTableStore from '@store/table-store/table.store.ts';
import ViewControls from '@components/view-controls/view-controls.tsx';
import { ViewControlsEnum } from '@models/view-controls.model.ts';
import { LanguageEnum } from '@models/language.model.ts';
import { Button } from '@mui/material';
import AppDialog from '@shared/components/app-dialog/app-dialog.tsx';
import DeleteDialog from '@shared/components/delete-dialog/delete-dialog.tsx';
import { useTranslation } from 'react-i18next';
import { ICONS } from '@shared/components/icons/icon.tsx';
import { getMovies } from '@shared/api/entertainment-api/movies-api.ts';
import useMoviesStore from '../../store/movies.store.ts';
import { MoviesRowDataModel } from '@modules/entertainment/models/movies.model.ts';
import { SortOrder, TriggerTypeEnum } from '@shared/models/table.model.ts';
import { useSearchParams } from 'react-router-dom';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';
import MovieDialogContent from '@modules/entertainment/pages/movies/compnents/movie-dialog-content/movie-dialog-content.tsx';
import { moviesColumns } from '@modules/entertainment/pages/movies/compnents/movies-table/components/columns/columns.tsx';
import { TABLE_HEIGHT } from '@utils/table-height.utils.ts';
import useTableSelection from '@hooks/use-table-selection.ts';
import { getRowIdKey } from '@utils/table-selection.utils.tsx';

const MoviesTable: React.FC = () => {
  const { createRow, editRow, deleteRow, isLoading, getGenreList, genreList } =
    useMoviesStore();
  const { resetAllSelectedMedia } = useMediaLibraryStore();
  const { t } = useTranslation();
  const tableStore = useMemo(() => createTableStore(), []);
  const {
    selectedKeys: selectedRows,
    isPageAllSelected,
    isPagePartiallySelected,
    isRowSelected,
    toggleRow: handleSelectRow,
    togglePage: handleSelectAll,
    clearSelection
  } = useTableSelection<MoviesModel>({
    tableStore,
    getRowKey: getRowIdKey
  });
  const [language, setLanguage] = useState(LanguageEnum.English);
  const [view, setView] = useState(ViewControlsEnum.List);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<MoviesRowDataModel | null>(
    null
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const count = selectedRows.length;

  const handleEdit = (row: any) => {
    setOpenDialog(true);
    setSelectedItem(row);
  };

  const handleDelete = (row: any) => {
    setShowDeleteDialog(true);
    setSelectedItem(row);
  };

  const handleDeleteConfirm = () => {
    deleteRow({
      id: selectedItem?._id ? selectedItem?._id : selectedRows,
      state: tableStore.getState(),
      handleClose
    });
  };

  const handleClose = () => {
    resetAllSelectedMedia();
    setShowDeleteDialog(false);
    setOpenDialog(false);
    setSelectedItem(null);
    clearSelection();
  };

  const handleTableTrigger = (type: TriggerTypeEnum) => {
    if (type === TriggerTypeEnum.Add) {
      setOpenDialog(true);
    }
  };

  const handleFormSave = (data: any) => {
    if (selectedItem?._id) {
      editRow({
        data,
        id: selectedItem?._id,
        state: tableStore.getState(),
        handleClose
      });
    } else {
      createRow({
        data,
        state: tableStore.getState(),
        handleClose
      });
    }
  };

  const handleAction = (action: { type: string; value?: string }) => {
    switch (action.type) {
      case ViewControlsEnum.View:
        setView(action.value as ViewControlsEnum);
        break;
      case ViewControlsEnum.Delete:
        handleDelete(selectedRows);
        break;
      case ViewControlsEnum.ImportPlaylist:
        break;
      case ViewControlsEnum.SelectAll:
        break;
      case ViewControlsEnum.Search:
        break;
      case ViewControlsEnum.Language:
        setLanguage(action.value as LanguageEnum);
        break;
    }
  };

  const columns = moviesColumns(
    handleEdit,
    handleDelete,
    {
      handleSelectRow,
      handleSelectAll,
      isPageAllSelected,
      isPagePartiallySelected,
      isRowSelected
    },
    language
  );

  useEffect(() => {
    const fetchDataAndOpenDialog = async () => {
      getGenreList();

      const dialog = searchParams.get('dialog');
      if (dialog === 'open') {
        setOpenDialog(true);
      }
    };

    fetchDataAndOpenDialog().then();
  }, []);

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-end mb-4">
        <ViewControls onAction={handleAction} selectedRows={selectedRows} />

        <Button
          variant="contained"
          color="primary"
          startIcon={ICONS.plusIcon}
          onClick={() => handleEdit(selectedItem)}
        >
          {t('add')}
        </Button>
      </div>

      <div className={TABLE_HEIGHT.WITH_TABS_AND_ACTIONS}>
        {view === ViewControlsEnum.List && (
          <DynamicTable
            tableStore={tableStore}
            apiFunction={getMovies}
            columns={columns}
            language={language}
            onTriggerAction={handleTableTrigger}
            tableKey="entertainment-movies-table"
            initialState={{
              sort: ['date', SortOrder.Descending]
            }}
          />
        )}
      </div>

      <div className="w-full h-full">
        <AppDialog
          open={openDialog}
          title={t('entertainment_movies_title')}
          size="xl"
          content={
            <MovieDialogContent
              genreList={genreList}
              selectedItem={selectedItem}
              onSave={handleFormSave}
            />
          }
          onClose={handleClose}
        />

        <DeleteDialog
          onCancel={handleClose}
          onDelete={handleDeleteConfirm}
          open={showDeleteDialog}
          isLoading={isLoading()}
          title={
            selectedItem?._id
              ? t('delete_popup_title')
              : t('delete_popup_title_many_items', { count })
          }
        />
      </div>
    </div>
  );
};

export default MoviesTable;
